import type { FC } from 'react';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { Show } from '@components/Show';
import { useContractFormPreventableLink } from '@ContractBuilder/hooks/useContractFormPreventableLink';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import { useDocumentLocation } from '@hooks/useDocumentLocation';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import sharedClasses from '@root/src/utils/shared-classes';
import { cva } from 'class-variance-authority';

import { UnsavedChangesModal } from '../../modules/block-edit/views/UnsavedChangesModal';
import { ViewPath } from '../constants';

const containerClasses = cva(
  [
    'flex',
    'w-fit',
    'overflow-hidden',
    'rounded-md',
    'text-center',
    'text-sm',
    'font-medium',
    'transition',
    'border',
    'border-primary-600',
  ],
  {
    variants: {
      disabled: {
        true: [...sharedClasses.disabledContainer, '!border-primary-600/50'],
      },
    },
  },
);
const buttonClasses = cva(
  [
    'border-r-primary-600 ',
    'border-r',
    'cursor-pointer',
    'hover:bg-info-200',
    'last:border-0',
    'px-4',
    'py-1',
    'text-primary-600',
    'whitespace-nowrap',
  ],
  {
    variants: {
      selected: {
        true: ['text-white bg-primary-600 hover:bg-primary-600'],
      },
      disabled: {
        true: sharedClasses.disabled,
        false: 'pointer-events-auto',
      },
    },
    defaultVariants: {
      selected: false,
      disabled: true,
    },
  },
);

export const ViewSelectionTabs: FC = () => {
  const location = useLocation();
  const isEditing = useBlockEditFormStore(({ formValues }) => formValues?.id !== undefined);
  const isSavingForm = useDatapointsStore(({ isSubmitting }) => isSubmitting);
  const isDisabled = isEditing || isSavingForm;

  const isRenewal = useEntityStore(({ submission }) => submission?.renewalParentId !== undefined);
  const { isContractView, isEndorsement, isEndorsementView, isFormView, isTemplate, isBlockComparisonView } =
    useDocumentLocation();
  const getFeatureFlag = useFeatureFlags();
  const hasEndorsementsFeature = getFeatureFlag('Endorsements');
  const hasTemplateDefaultContentFeature = getFeatureFlag('TemplateDefaultContent');

  const { isBlocked, onDisregardChanges, onLinkClick, onReset, onSaveChanges } = useContractFormPreventableLink();

  const handleReset = useCallback(() => {
    useBlockEditFormStore.getState().reset();
  }, []);

  useUpdateEffect(() => {
    // Reset state when navigating between tabs
    handleReset();
    useMultiSelectStore.getState().clearSelection();
  }, [location.pathname, handleReset]);

  const formViewTab = (
    <button
      type="button"
      role="tab"
      aria-selected={isFormView}
      disabled={isDisabled}
      onClick={() => onLinkClick(ViewPath.Form)}
      className={buttonClasses({ selected: isFormView, disabled: isDisabled })}
    >
      {isTemplate ? 'Default Content' : 'Form View'}
    </button>
  );

  const contractViewTab = (
    <button
      type="button"
      role="tab"
      aria-selected={isContractView && !isEndorsementView}
      disabled={isDisabled}
      onClick={() => onLinkClick(ViewPath.Contract)}
      className={buttonClasses({ selected: isContractView && !isEndorsementView, disabled: isDisabled })}
    >
      {isTemplate ? 'Template View' : 'Contract View'}
    </button>
  );

  const isSwitcherHidden = isTemplate && !hasTemplateDefaultContentFeature;

  return (
    <>
      <Show when={!isSwitcherHidden}>
        <div
          className={containerClasses({
            disabled: isDisabled,
          })}
          id="contract-actions"
          role="tablist"
        >
          <>
            {isTemplate ? (
              <>
                {contractViewTab}
                {formViewTab}
              </>
            ) : (
              <>
                {formViewTab}
                {contractViewTab}
              </>
            )}
            <Show when={isEndorsement && hasEndorsementsFeature}>
              <button
                type="button"
                role="tab"
                aria-selected={isEndorsementView}
                disabled={isDisabled}
                onClick={() => onLinkClick(ViewPath.EndorsementView)}
                className={buttonClasses({ selected: isEndorsementView, disabled: isDisabled })}
              >
                Endorsement View
              </button>
            </Show>
            <Show when={isRenewal}>
              <button
                type="button"
                role="tab"
                aria-selected={isBlockComparisonView}
                disabled={isDisabled}
                onClick={() => onLinkClick(ViewPath.ComparisonView)}
                className={buttonClasses({ selected: isBlockComparisonView, disabled: isDisabled })}
              >
                Comparison View
              </button>
            </Show>
          </>
        </div>
      </Show>
      <UnsavedChangesModal
        isOpen={isBlocked}
        onCancel={onReset}
        onDisregardChanges={onDisregardChanges}
        onSaveChanges={onSaveChanges}
      />
    </>
  );
};
